// src/components/AnamneseScreen.jsx
// Tela premium/mobile-first de anamnese clínica.
// Fluxo seguro: preenchimento pela psicóloga, em fases, com rascunho/finalização/reabertura.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileClock,
  FileText,
  Flag,
  HeartPulse,
  LoaderCircle,
  LockKeyhole,
  Menu,
  Plus,
  RotateCcw,
  Save,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/anamnese.scss";

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const APP_ROUTES = {
  CLIENTS: "/clients",
  CLIENT_DETAILS: "/clients/details",
};

const API_BASE = String(
  process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const PHASES = [
  {
    key: "inicio",
    label: "Início",
    title: "Identificação e demanda",
    desc: "Estado civil, religião, escolaridade, contexto familiar, queixa principal e expectativa terapêutica.",
  },
  {
    key: "saude",
    label: "Saúde",
    title: "Histórico, saúde e medicações",
    desc: "Histórico psicológico, psiquiátrico, medicações e profissionais envolvidos.",
  },
  {
    key: "familia",
    label: "Família",
    title: "Família, rotina, trabalho e estudo",
    desc: "Composição familiar, filhos, trabalho, estudo, rotina e hábitos.",
  },
  {
    key: "eventos",
    label: "Risco",
    title: "Eventos marcantes e risco",
    desc: "Perdas, eventos relevantes, ideação suicida, automutilação e plano de segurança.",
  },
  {
    key: "finalizacao",
    label: "Conclusão",
    title: "Objetivos e fechamento clínico",
    desc: "Objetivos terapêuticos, impressões iniciais, encaminhamentos e observações finais.",
  },
];

function PhaseIcon({
  phaseKey,
  size = 16,
}) {
  const icons = {
    inicio: UserRound,
    saude: HeartPulse,
    familia: UsersRound,
    eventos: ShieldAlert,
    finalizacao: Flag,
  };

  const Icon =
    icons[phaseKey] || FileText;

  return (
    <Icon
      size={size}
      aria-hidden="true"
    />
  );
}

const EMPTY_MEDICACAO = {
  nome: "",
  dosagem: "",
  frequencia: "",
  desdeQuando: "",
  prescritoPor: "",
  motivo: "",
  efeitosColaterais: "",
  observacoes: "",
};

const EMPTY_MEDICACAO_HISTORICO = {
  nome: "",
  periodoUso: "",
  idadeNaEpoca: "",
  motivo: "",
  porqueParou: "",
  teveEfeito: null,
  observacoes: "",
};

const EMPTY_PROFISSIONAL = {
  tipo: "",
  nome: "",
  especialidade: "",
  motivoAcompanhamento: "",
  desdeQuando: "",
  frequencia: "",
  contato: "",
  observacoes: "",
};

const EMPTY_FILHO = {
  nome: "",
  idade: "",
  moraComCliente: null,
  comQuemMora: "",
  observacoes: "",
};

const EMPTY_EVENTO = {
  tipo: "",
  descricao: "",
  quando: "",
  motivo: "",
  impacto: "",
  observacoes: "",
};

const INITIAL_FORM = {
  _id: "",
  status: "rascunho",

  estadoCivil: "",
  sexoIdentifica: "",
  religiao: "",
  grauInstrucao: "",
  comQuemMora: "",
  redeApoio: "",

  trabalhaAtualmente: null,
  trabalho: {
    profissao: "",
    local: "",
    funcao: "",
    cargaHoraria: "",
    satisfacaoTrabalho: "",
    conflitosTrabalho: "",
    afastadoInss: null,
    observacoes: "",
  },

  estudaAtualmente: null,
  estudo: {
    cursoSerie: "",
    instituicao: "",
    dificuldades: "",
    observacoes: "",
  },

  queixaPrincipal: "",
  quandoComecou: "",
  fatoresPiora: "",
  fatoresMelhora: "",
  frequenciaSintomas: "",
  intensidadeSintomas: "",
  impactoRotina: "",
  objetivosTerapiaCliente: "",
  expectativasTratamento: "",

  jaPassouPsicologo: null,
  historicoPsicologico: {
    periodo: "",
    idadeNaEpoca: "",
    motivo: "",
    tempoAcompanhamento: "",
    experiencia: "",
    teveAlta: null,
    motivoInterrupcao: "",
    observacoes: "",
  },

  jaTeveDiagnostico: null,
  diagnosticosAnteriores: "",
  jaTeveInternacaoPsiquiatrica: null,
  internacaoPsiquiatricaDetalhes: "",

  usaMedicacaoAtualmente: null,
  medicacoesAtuais: [],
  jaUsouMedicacaoAntes: null,
  historicoMedicacoes: [],

  acompanhaPsiquiatra: null,
  psiquiatra: {
    nome: "",
    contato: "",
    frequencia: "",
    observacoes: "",
  },

  outrosProfissionais: [],

  possuiDoencasPreexistentes: null,
  doencasPreexistentes: "",
  alergias: "",
  cirurgias: "",
  internacoes: "",
  dorCronica: "",
  deficienciaOuLimitacao: "",
  acompanhamentoMedico: "",

  rotina: "",
  sono: {
    qualidade: "",
    horasPorNoite: "",
    dificuldades: "",
    observacoes: "",
  },
  alimentacao: {
    qualidade: "",
    apetite: "",
    observacoes: "",
  },
  atividadeFisica: {
    pratica: null,
    frequencia: "",
    observacoes: "",
  },
  usoSubstancias: {
    alcool: null,
    alcoolDetalhes: "",
    tabaco: null,
    tabacoDetalhes: "",
    outrasDrogas: null,
    outrasDrogasDetalhes: "",
    observacoes: "",
  },

  casadoOuRelacionamento: null,
  companheiro: {
    nome: "",
    idade: "",
    tempoRelacionamento: "",
    qualidadeRelacionamento: "",
    observacoes: "",
  },

  temFilhos: null,
  filhos: [],

  relacaoPai: "",
  relacaoMae: "",
  relacaoIrmaos: "",
  relacaoFilhos: "",
  conflitosFamiliares: "",

  possuiHistoricoDoencasFamilia: null,
  historicoDoencasFamilia: "",
  historicoTranstornosFamilia: "",
  historicoDependenciaQuimicaFamilia: "",
  historicoSuicidioFamilia: "",
  historicoViolenciaFamilia: "",

  teveMorteEnteQuerido: null,
  mortesEntesQueridos: [],
  teveDesempregoFamilia: null,
  desempregosFamilia: [],
  eventosMarcantes: [],

  tevePensamentoSuicida: null,
  pensamentoSuicidaAtual: null,
  jaTentouSuicidio: null,
  tentativaSuicidioDetalhes: "",
  existePlanoSuicida: null,
  acessoAMeios: null,
  automutilacao: null,
  automutilacaoDetalhes: "",
  historicoViolencia: "",
  nivelRisco: "",
  planoSeguranca: "",
  observacoesRisco: "",

  objetivosTerapeuticos: "",
  impressoesIniciais: "",
  hipotesesIniciais: "",
  pontosAtencao: "",
  encaminhamentos: "",
  combinadosIniciais: "",
  observacoesFinais: "",
};

function readAuth(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();
  return clean ? clean.split(/\s+/)[0] : "";
}

function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "Não foi possível concluir a operação."
  );
}

function formatDateTimeBR(dateLike) {
  if (!dateLike) return "—";

  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function mergeDefaults(defaults, values) {
  if (Array.isArray(defaults)) {
    return Array.isArray(values) ? values : defaults;
  }

  if (!isPlainObject(defaults)) {
    return values ?? defaults;
  }

  const output = { ...defaults };

  if (!isPlainObject(values)) {
    return output;
  }

  Object.keys(values).forEach((key) => {
    if (isPlainObject(defaults[key])) {
      output[key] = mergeDefaults(defaults[key], values[key]);
      return;
    }

    if (Array.isArray(defaults[key])) {
      output[key] = Array.isArray(values[key]) ? values[key] : defaults[key];
      return;
    }

    output[key] = values[key] ?? defaults[key] ?? "";
  });

  return output;
}

function getByPath(source, path) {
  return String(path)
    .split(".")
    .reduce((current, key) => (current ? current[key] : undefined), source);
}

function setByPath(source, path, value) {
  const keys = String(path).split(".");
  const clone = Array.isArray(source) ? [...source] : { ...source };
  let current = clone;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
      return;
    }

    const next = current[key];
    current[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    current = current[key];
  });

  return clone;
}

function isFilled(value) {
  if (value === null || value === undefined) return false;

  if (typeof value === "boolean") return true;

  if (typeof value === "number") return Number.isFinite(value);

  if (typeof value === "string") return value.trim().length > 0;

  if (Array.isArray(value)) {
    return value.some((item) => isFilled(item));
  }

  if (typeof value === "object") {
    return Object.values(value).some((item) => isFilled(item));
  }

  return false;
}

function calculateLocalProgress(form) {
  const sections = [
    {
      key: "identificacao",
      label: "Identificação",
      done:
        isFilled(form.estadoCivil) ||
        isFilled(form.sexoIdentifica) ||
        isFilled(form.grauInstrucao) ||
        isFilled(form.comQuemMora),
    },
    {
      key: "queixa",
      label: "Queixa",
      done:
        isFilled(form.queixaPrincipal) ||
        isFilled(form.quandoComecou) ||
        isFilled(form.impactoRotina),
    },
    {
      key: "historico",
      label: "Histórico",
      done:
        isFilled(form.jaPassouPsicologo) ||
        isFilled(form.historicoPsicologico) ||
        isFilled(form.jaTeveDiagnostico),
    },
    {
      key: "medicacoes",
      label: "Medicações",
      done:
        isFilled(form.usaMedicacaoAtualmente) ||
        isFilled(form.medicacoesAtuais) ||
        isFilled(form.historicoMedicacoes),
    },
    {
      key: "profissionais",
      label: "Profissionais",
      done: isFilled(form.acompanhaPsiquiatra) || isFilled(form.outrosProfissionais),
    },
    {
      key: "familia",
      label: "Família",
      done:
        isFilled(form.casadoOuRelacionamento) ||
        isFilled(form.temFilhos) ||
        isFilled(form.filhos) ||
        isFilled(form.conflitosFamiliares),
    },
    {
      key: "rotina",
      label: "Rotina",
      done:
        isFilled(form.trabalhaAtualmente) ||
        isFilled(form.estudaAtualmente) ||
        isFilled(form.rotina) ||
        isFilled(form.sono),
    },
    {
      key: "eventos",
      label: "Eventos",
      done:
        isFilled(form.teveMorteEnteQuerido) ||
        isFilled(form.teveDesempregoFamilia) ||
        isFilled(form.eventosMarcantes),
    },
    {
      key: "risco",
      label: "Risco",
      done:
        isFilled(form.tevePensamentoSuicida) ||
        isFilled(form.pensamentoSuicidaAtual) ||
        isFilled(form.jaTentouSuicidio) ||
        isFilled(form.nivelRisco) ||
        isFilled(form.observacoesRisco),
    },
    {
      key: "objetivos",
      label: "Objetivos",
      done:
        isFilled(form.objetivosTerapeuticos) ||
        isFilled(form.impressoesIniciais) ||
        isFilled(form.observacoesFinais),
    },
  ];

  const total = sections.length;
  const done = sections.filter((section) => section.done).length;

  return {
    total,
    done,
    percent: Math.round((done / total) * 100),
    sections,
  };
}

function sanitizePayload(form) {
  const payload = {};
  const ignoredKeys = new Set(["_id", "status", "__v", "progresso", "clienteId", "psicologoId", "createdAt", "updatedAt"]);

  Object.keys(INITIAL_FORM).forEach((key) => {
    if (!ignoredKeys.has(key) && Object.prototype.hasOwnProperty.call(form, key)) {
      payload[key] = form[key];
    }
  });

  return payload;
}

async function apiRequest(path, { method = "GET", token, body } = {}) {
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = text ? { message: text } : null;
  }

  if (!response.ok) {
    const error = new Error(data?.message || `Erro HTTP ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

function StatusBadge({ status }) {
  const normalized = String(
    status || "rascunho"
  ).toLowerCase();

  const metadata = {
    rascunho: {
      label: "Rascunho",
      icon: FileClock,
    },
    finalizada: {
      label: "Finalizada",
      icon: CheckCircle2,
    },
    reaberta: {
      label: "Reaberta",
      icon: RotateCcw,
    },
  };

  const current =
    metadata[normalized] ||
    metadata.rascunho;

  const Icon = current.icon;

  return (
    <span
      className={`ana-status ana-status--${normalized}`}
    >
      <Icon
        size={13}
        aria-hidden="true"
      />
      {current.label}
    </span>
  );
}

function Field({ label, value, onChange, placeholder, disabled, type = "text", hint }) {
  return (
    <label className="ana-field">
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
      />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder, disabled, rows = 4, hint }) {
  return (
    <label className="ana-field ana-field--textarea">
      <span>{label}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
      />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function SelectField({ label, value, onChange, options, disabled, hint }) {
  return (
    <label className="ana-field">
      <span>{label}</span>
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">Selecione</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function BoolField({ label, value, onChange, disabled, hint }) {
  const stringValue = value === true ? "true" : value === false ? "false" : "";

  return (
    <label className="ana-field">
      <span>{label}</span>
      <select
        value={stringValue}
        onChange={(event) => {
          const next = event.target.value;
          if (next === "true") onChange(true);
          else if (next === "false") onChange(false);
          else onChange(null);
        }}
        disabled={disabled}
      >
        <option value="">Não informado</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </select>
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function Section({ eyebrow, title, desc, children, tone = "default" }) {
  return (
    <section className={`ana-section ana-section--${tone}`}>
      <div className="ana-section__head">
        <span>{eyebrow}</span>
        <h2>{title}</h2>
        {desc ? <p>{desc}</p> : null}
      </div>

      <div className="ana-section__body">{children}</div>
    </section>
  );
}

function Repeater({ title, desc, items, emptyItem, disabled, onAdd, onRemove, children }) {
  return (
    <div className="ana-repeater">
      <div className="ana-repeater__head">
        <div>
          <strong>{title}</strong>
          {desc ? <span>{desc}</span> : null}
        </div>

        <button
          type="button"
          className="ana-mini-btn"
          onClick={() => onAdd(emptyItem)}
          disabled={disabled}
        >
          <Plus
            size={15}
            aria-hidden="true"
          />
          Adicionar
        </button>
      </div>

      {items?.length ? (
        <div className="ana-repeater__list">
          {items.map((item, index) => (
            <div className="ana-repeat-card" key={item._id || `${title}-${index}`}>
              <div className="ana-repeat-card__top">
                <span>{title} {index + 1}</span>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  disabled={disabled}
                >
                  <Trash2
                    size={14}
                    aria-hidden="true"
                  />
                  Remover
                </button>
              </div>

              {children(item, index)}
            </div>
          ))}
        </div>
      ) : (
        <div className="ana-empty-inline">Nenhum registro adicionado.</div>
      )}
    </div>
  );
}

function DashboardState({ title, detail, actionLabel, onAction }) {
  return (
    <div className="ana-state">
      <div className="ana-state__logo">
        <img src={Logo} alt="PsyAssist" />
      </div>

      <div
        className="ana-state__icon"
        aria-hidden="true"
      >
        <ClipboardCheck size={25} />
      </div>
      <h1>{title}</h1>
      <p>{detail}</p>
      {actionLabel ? (
        <button type="button" className="ana-btn ana-btn--primary" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export default function AnamneseScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const token = useMemo(() => readAuth(AUTH_KEYS.TOKEN), []);
  const userId = useMemo(() => readAuth(AUTH_KEYS.USER_ID), []);
  const userName = useMemo(() => readAuth(AUTH_KEYS.USER_NAME), []);
  const firstName = useMemo(() => getFirstName(userName), [userName]);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const clienteId = useMemo(() => {
    return (
      location.state?.clienteId ||
      location.state?.clientId ||
      location.state?.cliente?._id ||
      location.state?.client?._id ||
      params.get("clienteId") ||
      params.get("clientId") ||
      ""
    );
  }, [location.state, params]);

  const initialClient = useMemo(() => {
    return location.state?.cliente || location.state?.clientData || location.state?.client || null;
  }, [location.state]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cliente, setCliente] = useState(initialClient);
  const [exists, setExists] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const locked = form.status === "finalizada";
  const currentPhase = PHASES[phaseIndex];

  const progress = useMemo(() => calculateLocalProgress(form), [form]);
  const completedPhases = useMemo(() => Math.round(((phaseIndex + 1) / PHASES.length) * 100), [phaseIndex]);

  const showNotice = useCallback(
    (type, title, message) => {
      const tone = [
        "success",
        "warning",
        "error",
        "info",
      ].includes(type)
        ? type
        : "info";

      toast[tone]({
        title:
          title ||
          (tone === "success"
            ? "Operação concluída"
            : tone === "warning"
              ? "Atenção"
              : tone === "error"
                ? "Não foi possível concluir"
                : "Informação"),
        message: message || "",
        duration:
          tone === "error"
            ? 6500
            : 5200,
      });
    },
    [toast]
  );

  const update = useCallback((path, value) => {
    setForm((current) => setByPath(current, path, value));
  }, []);

  const updateArrayItem = useCallback((path, index, field, value) => {
    setForm((current) => {
      const list = Array.isArray(getByPath(current, path)) ? [...getByPath(current, path)] : [];
      list[index] = setByPath(list[index] || {}, field, value);
      return setByPath(current, path, list);
    });
  }, []);

  const addArrayItem = useCallback((path, emptyItem) => {
    setForm((current) => {
      const list = Array.isArray(getByPath(current, path)) ? [...getByPath(current, path)] : [];
      list.push({ ...emptyItem });
      return setByPath(current, path, list);
    });
  }, []);

  const removeArrayItem = useCallback((path, index) => {
    setForm((current) => {
      const list = Array.isArray(getByPath(current, path)) ? [...getByPath(current, path)] : [];
      list.splice(index, 1);
      return setByPath(current, path, list);
    });
  }, []);

  const loadAnamnese = useCallback(async () => {
    setLoading(true);

    try {
      if (!token || !userId) {
        throw new Error("Sessão expirada. Faça login novamente.");
      }

      if (!clienteId) {
        throw new Error("Cliente não informado. Abra a anamnese a partir da tela de detalhes do cliente.");
      }

      const response = await apiRequest(
        `/psy/anamnese/${clienteId}?id=${encodeURIComponent(userId)}`,
        { token }
      );

      setCliente(response?.cliente || initialClient || null);
      setExists(Boolean(response?.existe));

      if (response?.data) {
        setForm(mergeDefaults(INITIAL_FORM, response.data));
      } else {
        setForm(INITIAL_FORM);
      }
    } catch (error) {
      console.error("Erro ao carregar anamnese:", error);
      showNotice("error", "Não foi possível carregar", getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [clienteId, initialClient, showNotice, token, userId]);

  useEffect(() => {
    loadAnamnese();
  }, [loadAnamnese]);

  const saveDraft = useCallback(
    async ({ silent = false } = {}) => {
      if (locked) {
        showNotice("warning", "Anamnese finalizada", "Reabra a anamnese antes de editar.");
        return null;
      }

      setSaving(true);

      try {
        const payload = sanitizePayload(form);
        let response;

        if (exists && form._id) {
          response = await apiRequest(
            `/psy/anamnese/${form._id}?id=${encodeURIComponent(userId)}`,
            {
              method: "PUT",
              token,
              body: payload,
            }
          );
        } else {
          response = await apiRequest(`/psy/anamnese?id=${encodeURIComponent(userId)}`, {
            method: "POST",
            token,
            body: {
              ...payload,
              clienteId,
            },
          });
        }

        const data = response?.data;

        if (data) {
          setForm(mergeDefaults(INITIAL_FORM, data));
          setExists(true);
        }

        if (!silent) {
          showNotice("success", "Rascunho salvo", "As informações da anamnese foram salvas.");
        }

        return data;
      } catch (error) {
        console.error("Erro ao salvar anamnese:", error);
        showNotice("error", "Erro ao salvar", getErrorMessage(error));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [clienteId, exists, form, locked, showNotice, token, userId]
  );

  const finalize = useCallback(async () => {
    if (locked) return;

    setSaving(true);

    try {
      const saved = await saveDraft({ silent: true });
      const id = saved?._id || form._id;

      if (!id) {
        throw new Error("Salve a anamnese antes de finalizar.");
      }

      const response = await apiRequest(
        `/psy/anamnese/${id}/finalizar?id=${encodeURIComponent(userId)}`,
        {
          method: "PATCH",
          token,
        }
      );

      if (response?.data) {
        setForm(mergeDefaults(INITIAL_FORM, response.data));
        setExists(true);
      }

      showNotice(
        "success",
        "Anamnese finalizada",
        "A anamnese foi marcada como finalizada. Você pode reabrir se precisar complementar depois."
      );
    } catch (error) {
      console.error("Erro ao finalizar anamnese:", error);
      showNotice("error", "Erro ao finalizar", getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }, [form._id, locked, saveDraft, showNotice, token, userId]);

  const reopen = useCallback(async () => {
    if (!form._id) return;

    setSaving(true);

    try {
      const response = await apiRequest(
        `/psy/anamnese/${form._id}/reabrir?id=${encodeURIComponent(userId)}`,
        {
          method: "PATCH",
          token,
        }
      );

      if (response?.data) {
        setForm(mergeDefaults(INITIAL_FORM, response.data));
      }

      showNotice("success", "Anamnese reaberta", "Agora você pode continuar editando a anamnese.");
    } catch (error) {
      console.error("Erro ao reabrir anamnese:", error);
      showNotice("error", "Erro ao reabrir", getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }, [form._id, showNotice, token, userId]);

  const goBack = useCallback(() => {
    if (clienteId) {
      navigate(APP_ROUTES.CLIENT_DETAILS, {
        state: {
          clienteId,
          clientId: clienteId,
          cliente,
          clientData: cliente,
        },
      });
      return;
    }

    navigate(APP_ROUTES.CLIENTS);
  }, [cliente, clienteId, navigate]);

  const nextPhase = useCallback(async () => {
    if (!locked) {
      await saveDraft({ silent: true });
    }

    setPhaseIndex((current) => Math.min(current + 1, PHASES.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [locked, saveDraft]);

  const previousPhase = useCallback(() => {
    setPhaseIndex((current) => Math.max(current - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (loading) {
    return (
      <div className="ana-shell ana-shell--dark">
        <DashboardState
          title="Carregando anamnese..."
          detail="Buscando os dados clínicos iniciais deste cliente."
        />
      </div>
    );
  }

  return (
    <div className="ana-shell ana-shell--dark">
      <div className="ana-orbs" aria-hidden="true">
        <span className="ana-orb ana-orb--a" />
        <span className="ana-orb ana-orb--b" />
        <span className="ana-orb ana-orb--c" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="ana-topbar">
        <button
          type="button"
          className="ana-icon-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu
            size={19}
            aria-hidden="true"
          />
        </button>

        <div className="ana-brand">
          <img src={Logo} alt="PsyAssist" />
          <div>
            <strong>Anamnese</strong>
            <span>{cliente?.nome || "Cliente"}</span>
          </div>
        </div>

        <div className="ana-topbar__actions">
          <button
            type="button"
            className="ana-btn ana-btn--ghost"
            onClick={goBack}
          >
            <ArrowLeft
              size={16}
              aria-hidden="true"
            />
            Voltar ao cliente
          </button>
          <button
            type="button"
            className="ana-btn ana-btn--soft"
            onClick={() => saveDraft()}
            disabled={saving || locked}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="ana-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save
                  size={16}
                  aria-hidden="true"
                />
                Salvar rascunho
              </>
            )}
          </button>
        </div>
      </header>

      <main className="ana-main">
        {!clienteId ? (
          <DashboardState
            title="Cliente não informado"
            detail="Abra esta tela a partir do detalhe do cliente para iniciar ou continuar a anamnese."
            actionLabel="Voltar para clientes"
            onAction={() => navigate(APP_ROUTES.CLIENTS)}
          />
        ) : (
          <div className="ana-layout">
            <aside className="ana-side ana-glass">
              <div className="ana-side__client">
                <div className="ana-side__client-icon">
                  <UserRound
                    size={21}
                    aria-hidden="true"
                  />
                </div>

                <div className="ana-side__client-copy">
                  <span>Cliente</span>
                  <strong>{cliente?.nome || "Cliente"}</strong>
                  <small>
                    {cliente?.email ||
                      cliente?.telefone ||
                      "Anamnese clínica inicial"}
                  </small>
                </div>
              </div>

              <div className="ana-side__status">
                <StatusBadge status={form.status} />
                <span>{exists ? "Anamnese existente" : "Nova anamnese"}</span>
              </div>

              <div className="ana-progress-card">
                <div>
                  <span>Preenchimento</span>
                  <strong>{progress.percent}%</strong>
                </div>
                <div className="ana-progress">
                  <i style={{ width: `${progress.percent}%` }} />
                </div>
                <small>
                  {progress.done} de {progress.total} blocos com alguma informação.
                </small>
              </div>

              <div className="ana-phase-list">
                {PHASES.map((phase, index) => (
                  <button
                    key={phase.key}
                    type="button"
                    className={index === phaseIndex ? "active" : ""}
                    onClick={() => setPhaseIndex(index)}
                  >
                    <b>
                      <PhaseIcon
                        phaseKey={phase.key}
                        size={15}
                      />
                    </b>

                    <span>
                      <small>
                        Fase {index + 1}
                      </small>
                      {phase.label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="ana-side__meta">
                <span>Atualização</span>
                <strong>
                  {formatDateTimeBR(
                    form.updatedAt ||
                      form.createdAt
                  )}
                </strong>

                {form.finalizadaEm ? (
                  <small>
                    Finalizada em{" "}
                    {formatDateTimeBR(
                      form.finalizadaEm
                    )}
                  </small>
                ) : null}
              </div>

              <div className="ana-side__security">
                <ShieldCheck
                  size={18}
                  aria-hidden="true"
                />

                <div>
                  <strong>
                    Documento clínico
                  </strong>
                  <span>
                    Registre apenas informações
                    pertinentes ao acompanhamento.
                  </span>
                </div>
              </div>
            </aside>

            <section className="ana-content">
              <section className="ana-hero ana-glass">
                <div>
                  <span className="ana-eyebrow">
                    <PhaseIcon
                      phaseKey={currentPhase.key}
                      size={15}
                    />

                    {firstName
                      ? `${firstName}, etapa ${phaseIndex + 1} de ${PHASES.length}`
                      : `Etapa ${phaseIndex + 1} de ${PHASES.length}`}
                  </span>
                  <h1>{currentPhase.title}</h1>
                  <p>{currentPhase.desc}</p>
                </div>

                <div className="ana-hero__meter">
                  <span>Fases</span>
                  <strong>{completedPhases}%</strong>
                  <div className="ana-progress ana-progress--phase">
                    <i style={{ width: `${completedPhases}%` }} />
                  </div>
                </div>
              </section>

              {locked ? (
                <div className="ana-locked ana-glass">
                  <div className="ana-locked__icon">
                    <LockKeyhole
                      size={20}
                      aria-hidden="true"
                    />
                  </div>

                  <div className="ana-locked__copy">
                    <strong>Anamnese finalizada</strong>
                    <span>
                      A edição está bloqueada para preservar o documento. Reabra se precisar complementar.
                    </span>
                  </div>

                  <button type="button" className="ana-btn ana-btn--warning" onClick={reopen} disabled={saving}>
                    Reabrir edição
                  </button>
                </div>
              ) : null}

              {phaseIndex === 0 ? (
                <PhaseInicio form={form} update={update} disabled={locked} />
              ) : null}

              {phaseIndex === 1 ? (
                <PhaseSaude
                  form={form}
                  update={update}
                  updateArrayItem={updateArrayItem}
                  addArrayItem={addArrayItem}
                  removeArrayItem={removeArrayItem}
                  disabled={locked}
                />
              ) : null}

              {phaseIndex === 2 ? (
                <PhaseFamilia
                  form={form}
                  update={update}
                  updateArrayItem={updateArrayItem}
                  addArrayItem={addArrayItem}
                  removeArrayItem={removeArrayItem}
                  disabled={locked}
                />
              ) : null}

              {phaseIndex === 3 ? (
                <PhaseEventos
                  form={form}
                  update={update}
                  updateArrayItem={updateArrayItem}
                  addArrayItem={addArrayItem}
                  removeArrayItem={removeArrayItem}
                  disabled={locked}
                />
              ) : null}

              {phaseIndex === 4 ? (
                <PhaseFinalizacao form={form} update={update} progress={progress} disabled={locked} />
              ) : null}

              <div className="ana-bottom-nav ana-glass">
                <button
                  type="button"
                  className="ana-btn ana-btn--ghost"
                  onClick={previousPhase}
                  disabled={phaseIndex === 0}
                >
                  <ChevronLeft
                    size={16}
                    aria-hidden="true"
                  />
                  Voltar fase
                </button>

                <button
                  type="button"
                  className="ana-btn ana-btn--soft"
                  onClick={() => saveDraft()}
                  disabled={saving || locked}
                >
                  {saving ? (
                    <>
                      <LoaderCircle
                        size={16}
                        aria-hidden="true"
                        className="ana-icon-spin"
                      />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save
                        size={16}
                        aria-hidden="true"
                      />
                      Salvar
                    </>
                  )}
                </button>

                {phaseIndex < PHASES.length - 1 ? (
                  <button
                    type="button"
                    className="ana-btn ana-btn--primary"
                    onClick={nextPhase}
                    disabled={saving}
                  >
                    Próxima fase
                    <ChevronRight
                      size={16}
                      aria-hidden="true"
                    />
                  </button>
                ) : locked ? (
                  <button
                    type="button"
                    className="ana-btn ana-btn--warning"
                    onClick={reopen}
                    disabled={saving}
                  >
                    <RotateCcw
                      size={16}
                      aria-hidden="true"
                    />
                    Reabrir
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ana-btn ana-btn--primary"
                    onClick={finalize}
                    disabled={saving}
                  >
                    <CheckCircle2
                      size={17}
                      aria-hidden="true"
                    />
                    Finalizar anamnese
                  </button>
                )}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function PhaseInicio({ form, update, disabled }) {
  return (
    <div className="ana-stack">
      <Section
        eyebrow="Fase 1"
        title="Identificação clínica"
        desc="Campos opcionais, preenchidos conforme fizer sentido clínico para o caso."
      >
        <div className="ana-grid ana-grid--3">
          <SelectField
            label="Estado civil"
            value={form.estadoCivil}
            onChange={(value) => update("estadoCivil", value)}
            disabled={disabled}
            options={[
              { value: "Solteiro(a)", label: "Solteiro(a)" },
              { value: "Casado(a)", label: "Casado(a)" },
              { value: "União estável", label: "União estável" },
              { value: "Separado(a)", label: "Separado(a)" },
              { value: "Divorciado(a)", label: "Divorciado(a)" },
              { value: "Viúvo(a)", label: "Viúvo(a)" },
              { value: "Outro", label: "Outro" },
            ]}
          />

          <SelectField
            label="Sexo"
            value={form.sexoIdentifica}
            onChange={(value) => update("sexoIdentifica", value)}
            disabled={disabled}
            options={[
              { value: "Masculino", label: "Masculino" },
              { value: "Feminino", label: "Feminino" },
            ]}
          />


          <Field
            label="Religião / espiritualidade"
            value={form.religiao}
            onChange={(value) => update("religiao", value)}
            disabled={disabled}
          />

          <Field
            label="Grau de instrução"
            value={form.grauInstrucao}
            onChange={(value) => update("grauInstrucao", value)}
            disabled={disabled}
            placeholder="Ex.: Ensino superior completo"
          />
        </div>

        <div className="ana-grid ana-grid--2">
          <TextArea
            label="Com quem o cliente mora?"
            value={form.comQuemMora}
            onChange={(value) => update("comQuemMora", value)}
            disabled={disabled}
            rows={3}
            placeholder="Ex.: Mora com esposo e dois filhos."
          />

          <TextArea
            label="Rede de apoio"
            value={form.redeApoio}
            onChange={(value) => update("redeApoio", value)}
            disabled={disabled}
            rows={3}
            placeholder="Familiares, amigos, instituições, cuidadores..."
          />
        </div>
      </Section>

      <Section
        eyebrow="Demanda"
        title="Queixa principal e expectativa"
        desc="Registre a demanda do cliente com linguagem clínica, sem precisar fechar diagnóstico neste momento."
      >
        <TextArea
          label="Queixa principal"
          value={form.queixaPrincipal}
          onChange={(value) => update("queixaPrincipal", value)}
          disabled={disabled}
          rows={5}
          placeholder="Ex.: Relata ansiedade intensa, dificuldade para dormir e preocupação constante."
        />

        <div className="ana-grid ana-grid--2">
          <TextArea
            label="Quando começou?"
            value={form.quandoComecou}
            onChange={(value) => update("quandoComecou", value)}
            disabled={disabled}
            rows={3}
          />

          <TextArea
            label="Impacto na rotina"
            value={form.impactoRotina}
            onChange={(value) => update("impactoRotina", value)}
            disabled={disabled}
            rows={3}
          />

          <TextArea
            label="O que piora?"
            value={form.fatoresPiora}
            onChange={(value) => update("fatoresPiora", value)}
            disabled={disabled}
            rows={3}
          />

          <TextArea
            label="O que melhora?"
            value={form.fatoresMelhora}
            onChange={(value) => update("fatoresMelhora", value)}
            disabled={disabled}
            rows={3}
          />

          <Field
            label="Frequência dos sintomas"
            value={form.frequenciaSintomas}
            onChange={(value) => update("frequenciaSintomas", value)}
            disabled={disabled}
            placeholder="Ex.: diariamente, semanalmente..."
          />

          <Field
            label="Intensidade percebida"
            value={form.intensidadeSintomas}
            onChange={(value) => update("intensidadeSintomas", value)}
            disabled={disabled}
            placeholder="Ex.: leve, moderada, intensa..."
          />
        </div>

        <div className="ana-grid ana-grid--2">
          <TextArea
            label="Objetivos do cliente com a terapia"
            value={form.objetivosTerapiaCliente}
            onChange={(value) => update("objetivosTerapiaCliente", value)}
            disabled={disabled}
            rows={3}
          />

          <TextArea
            label="Expectativas com o tratamento"
            value={form.expectativasTratamento}
            onChange={(value) => update("expectativasTratamento", value)}
            disabled={disabled}
            rows={3}
          />
        </div>
      </Section>
    </div>
  );
}

function PhaseSaude({ form, update, updateArrayItem, addArrayItem, removeArrayItem, disabled }) {
  return (
    <div className="ana-stack">
      <Section eyebrow="Histórico" title="Histórico psicológico e psiquiátrico">
        <div className="ana-grid ana-grid--3">
          <BoolField
            label="Já passou por psicólogo?"
            value={form.jaPassouPsicologo}
            onChange={(value) => update("jaPassouPsicologo", value)}
            disabled={disabled}
          />

          <BoolField
            label="Já teve diagnóstico?"
            value={form.jaTeveDiagnostico}
            onChange={(value) => update("jaTeveDiagnostico", value)}
            disabled={disabled}
          />

          <BoolField
            label="Já teve internação psiquiátrica?"
            value={form.jaTeveInternacaoPsiquiatrica}
            onChange={(value) => update("jaTeveInternacaoPsiquiatrica", value)}
            disabled={disabled}
          />
        </div>

        {form.jaPassouPsicologo === true ? (
          <div className="ana-subpanel">
            <h3>Detalhes do acompanhamento psicológico anterior</h3>
            <div className="ana-grid ana-grid--2">
              <Field label="Período" value={form.historicoPsicologico.periodo} onChange={(v) => update("historicoPsicologico.periodo", v)} disabled={disabled} />
              <Field label="Idade na época" value={form.historicoPsicologico.idadeNaEpoca} onChange={(v) => update("historicoPsicologico.idadeNaEpoca", v)} disabled={disabled} />
              <Field label="Motivo" value={form.historicoPsicologico.motivo} onChange={(v) => update("historicoPsicologico.motivo", v)} disabled={disabled} />
              <Field label="Tempo de acompanhamento" value={form.historicoPsicologico.tempoAcompanhamento} onChange={(v) => update("historicoPsicologico.tempoAcompanhamento", v)} disabled={disabled} />
              <BoolField label="Teve alta?" value={form.historicoPsicologico.teveAlta} onChange={(v) => update("historicoPsicologico.teveAlta", v)} disabled={disabled} />
              <Field label="Motivo da interrupção/alta" value={form.historicoPsicologico.motivoInterrupcao} onChange={(v) => update("historicoPsicologico.motivoInterrupcao", v)} disabled={disabled} />
            </div>
            <TextArea label="Como foi a experiência?" value={form.historicoPsicologico.experiencia} onChange={(v) => update("historicoPsicologico.experiencia", v)} disabled={disabled} rows={3} />
            <TextArea label="Observações" value={form.historicoPsicologico.observacoes} onChange={(v) => update("historicoPsicologico.observacoes", v)} disabled={disabled} rows={3} />
          </div>
        ) : null}

        {form.jaTeveDiagnostico === true ? (
          <TextArea label="Diagnósticos anteriores relatados" value={form.diagnosticosAnteriores} onChange={(v) => update("diagnosticosAnteriores", v)} disabled={disabled} rows={3} />
        ) : null}

        {form.jaTeveInternacaoPsiquiatrica === true ? (
          <TextArea label="Detalhes da internação" value={form.internacaoPsiquiatricaDetalhes} onChange={(v) => update("internacaoPsiquiatricaDetalhes", v)} disabled={disabled} rows={3} />
        ) : null}
      </Section>

      <Section eyebrow="Medicações" title="Medicações atuais e histórico de uso">
        <div className="ana-grid ana-grid--2">
          <BoolField label="Usa medicação atualmente?" value={form.usaMedicacaoAtualmente} onChange={(v) => update("usaMedicacaoAtualmente", v)} disabled={disabled} />
          <BoolField label="Já usou medicação antes?" value={form.jaUsouMedicacaoAntes} onChange={(v) => update("jaUsouMedicacaoAntes", v)} disabled={disabled} />
        </div>

        {form.usaMedicacaoAtualmente === true ? (
          <Repeater
            title="Medicação atual"
            desc="Inclua nome, dose, frequência e quem prescreveu."
            items={form.medicacoesAtuais}
            emptyItem={EMPTY_MEDICACAO}
            disabled={disabled}
            onAdd={(empty) => addArrayItem("medicacoesAtuais", empty)}
            onRemove={(index) => removeArrayItem("medicacoesAtuais", index)}
          >
            {(_, index) => (
              <div className="ana-grid ana-grid--2">
                <Field label="Nome" value={form.medicacoesAtuais[index]?.nome} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "nome", v)} disabled={disabled} />
                <Field label="Dosagem" value={form.medicacoesAtuais[index]?.dosagem} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "dosagem", v)} disabled={disabled} />
                <Field label="Frequência" value={form.medicacoesAtuais[index]?.frequencia} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "frequencia", v)} disabled={disabled} />
                <Field label="Desde quando" value={form.medicacoesAtuais[index]?.desdeQuando} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "desdeQuando", v)} disabled={disabled} />
                <Field label="Prescrito por" value={form.medicacoesAtuais[index]?.prescritoPor} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "prescritoPor", v)} disabled={disabled} />
                <Field label="Motivo" value={form.medicacoesAtuais[index]?.motivo} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "motivo", v)} disabled={disabled} />
                <TextArea label="Efeitos colaterais" value={form.medicacoesAtuais[index]?.efeitosColaterais} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "efeitosColaterais", v)} disabled={disabled} rows={2} />
                <TextArea label="Observações" value={form.medicacoesAtuais[index]?.observacoes} onChange={(v) => updateArrayItem("medicacoesAtuais", index, "observacoes", v)} disabled={disabled} rows={2} />
              </div>
            )}
          </Repeater>
        ) : null}

        {form.jaUsouMedicacaoAntes === true ? (
          <Repeater
            title="Histórico de medicação"
            desc="Registre medicações utilizadas anteriormente."
            items={form.historicoMedicacoes}
            emptyItem={EMPTY_MEDICACAO_HISTORICO}
            disabled={disabled}
            onAdd={(empty) => addArrayItem("historicoMedicacoes", empty)}
            onRemove={(index) => removeArrayItem("historicoMedicacoes", index)}
          >
            {(_, index) => (
              <div className="ana-grid ana-grid--2">
                <Field label="Nome" value={form.historicoMedicacoes[index]?.nome} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "nome", v)} disabled={disabled} />
                <Field label="Período de uso" value={form.historicoMedicacoes[index]?.periodoUso} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "periodoUso", v)} disabled={disabled} />
                <Field label="Idade na época" value={form.historicoMedicacoes[index]?.idadeNaEpoca} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "idadeNaEpoca", v)} disabled={disabled} />
                <Field label="Motivo" value={form.historicoMedicacoes[index]?.motivo} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "motivo", v)} disabled={disabled} />
                <Field label="Por que parou?" value={form.historicoMedicacoes[index]?.porqueParou} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "porqueParou", v)} disabled={disabled} />
                <BoolField label="Teve efeito?" value={form.historicoMedicacoes[index]?.teveEfeito} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "teveEfeito", v)} disabled={disabled} />
                <TextArea label="Observações" value={form.historicoMedicacoes[index]?.observacoes} onChange={(v) => updateArrayItem("historicoMedicacoes", index, "observacoes", v)} disabled={disabled} rows={2} />
              </div>
            )}
          </Repeater>
        ) : null}
      </Section>

      <Section eyebrow="Rede de cuidado" title="Psiquiatra e outros profissionais">
        <BoolField label="Acompanha psiquiatra?" value={form.acompanhaPsiquiatra} onChange={(v) => update("acompanhaPsiquiatra", v)} disabled={disabled} />

        {form.acompanhaPsiquiatra === true ? (
          <div className="ana-grid ana-grid--2">
            <Field label="Nome" value={form.psiquiatra.nome} onChange={(v) => update("psiquiatra.nome", v)} disabled={disabled} />
            <Field label="Contato" value={form.psiquiatra.contato} onChange={(v) => update("psiquiatra.contato", v)} disabled={disabled} />
            <Field label="Frequência" value={form.psiquiatra.frequencia} onChange={(v) => update("psiquiatra.frequencia", v)} disabled={disabled} />
            <TextArea label="Observações" value={form.psiquiatra.observacoes} onChange={(v) => update("psiquiatra.observacoes", v)} disabled={disabled} rows={2} />
          </div>
        ) : null}

        <Repeater
          title="Profissional"
          desc="Neurologista, T.O, fono, nutricionista, pediatra, psicopedagogo ou outros."
          items={form.outrosProfissionais}
          emptyItem={EMPTY_PROFISSIONAL}
          disabled={disabled}
          onAdd={(empty) => addArrayItem("outrosProfissionais", empty)}
          onRemove={(index) => removeArrayItem("outrosProfissionais", index)}
        >
          {(_, index) => (
            <div className="ana-grid ana-grid--2">
              <Field label="Tipo" value={form.outrosProfissionais[index]?.tipo} onChange={(v) => updateArrayItem("outrosProfissionais", index, "tipo", v)} disabled={disabled} placeholder="Ex.: Neurologista, T.O, Fono..." />
              <Field label="Nome" value={form.outrosProfissionais[index]?.nome} onChange={(v) => updateArrayItem("outrosProfissionais", index, "nome", v)} disabled={disabled} />
              <Field label="Especialidade" value={form.outrosProfissionais[index]?.especialidade} onChange={(v) => updateArrayItem("outrosProfissionais", index, "especialidade", v)} disabled={disabled} />
              <Field label="Desde quando" value={form.outrosProfissionais[index]?.desdeQuando} onChange={(v) => updateArrayItem("outrosProfissionais", index, "desdeQuando", v)} disabled={disabled} />
              <Field label="Frequência" value={form.outrosProfissionais[index]?.frequencia} onChange={(v) => updateArrayItem("outrosProfissionais", index, "frequencia", v)} disabled={disabled} />
              <Field label="Contato" value={form.outrosProfissionais[index]?.contato} onChange={(v) => updateArrayItem("outrosProfissionais", index, "contato", v)} disabled={disabled} />
              <TextArea label="Motivo do acompanhamento" value={form.outrosProfissionais[index]?.motivoAcompanhamento} onChange={(v) => updateArrayItem("outrosProfissionais", index, "motivoAcompanhamento", v)} disabled={disabled} rows={2} />
              <TextArea label="Observações" value={form.outrosProfissionais[index]?.observacoes} onChange={(v) => updateArrayItem("outrosProfissionais", index, "observacoes", v)} disabled={disabled} rows={2} />
            </div>
          )}
        </Repeater>
      </Section>

      <Section eyebrow="Saúde física" title="Doenças preexistentes e histórico médico">
        <BoolField label="Possui doenças preexistentes?" value={form.possuiDoencasPreexistentes} onChange={(v) => update("possuiDoencasPreexistentes", v)} disabled={disabled} />

        <div className="ana-grid ana-grid--2">
          {form.possuiDoencasPreexistentes === true ? (
            <TextArea label="Quais doenças preexistentes?" value={form.doencasPreexistentes} onChange={(v) => update("doencasPreexistentes", v)} disabled={disabled} rows={3} />
          ) : null}
          <TextArea label="Alergias" value={form.alergias} onChange={(v) => update("alergias", v)} disabled={disabled} rows={3} />
          <TextArea label="Cirurgias" value={form.cirurgias} onChange={(v) => update("cirurgias", v)} disabled={disabled} rows={3} />
          <TextArea label="Internações" value={form.internacoes} onChange={(v) => update("internacoes", v)} disabled={disabled} rows={3} />
          <TextArea label="Dor crônica" value={form.dorCronica} onChange={(v) => update("dorCronica", v)} disabled={disabled} rows={3} />
          <TextArea label="Deficiência ou limitação" value={form.deficienciaOuLimitacao} onChange={(v) => update("deficienciaOuLimitacao", v)} disabled={disabled} rows={3} />
          <TextArea label="Acompanhamento médico" value={form.acompanhamentoMedico} onChange={(v) => update("acompanhamentoMedico", v)} disabled={disabled} rows={3} />
        </div>
      </Section>
    </div>
  );
}

function PhaseFamilia({ form, update, updateArrayItem, addArrayItem, removeArrayItem, disabled }) {
  return (
    <div className="ana-stack">
      <Section eyebrow="Relacionamentos" title="Família, companheiro(a) e filhos">
        <div className="ana-grid ana-grid--2">
          <BoolField label="É casado(a) ou está em relacionamento?" value={form.casadoOuRelacionamento} onChange={(v) => update("casadoOuRelacionamento", v)} disabled={disabled} />
          <BoolField label="Tem filhos?" value={form.temFilhos} onChange={(v) => update("temFilhos", v)} disabled={disabled} />
        </div>

        {form.casadoOuRelacionamento === true ? (
          <div className="ana-subpanel">
            <h3>Companheiro(a)</h3>
            <div className="ana-grid ana-grid--2">
              <Field label="Nome" value={form.companheiro.nome} onChange={(v) => update("companheiro.nome", v)} disabled={disabled} />
              <Field label="Idade" value={form.companheiro.idade} onChange={(v) => update("companheiro.idade", v)} disabled={disabled} />
              <Field label="Tempo de relacionamento" value={form.companheiro.tempoRelacionamento} onChange={(v) => update("companheiro.tempoRelacionamento", v)} disabled={disabled} />
              <Field label="Qualidade do relacionamento" value={form.companheiro.qualidadeRelacionamento} onChange={(v) => update("companheiro.qualidadeRelacionamento", v)} disabled={disabled} />
              <TextArea label="Observações" value={form.companheiro.observacoes} onChange={(v) => update("companheiro.observacoes", v)} disabled={disabled} rows={3} />
            </div>
          </div>
        ) : null}

        {form.temFilhos === true ? (
          <Repeater
            title="Filho(a)"
            desc="Informe quantidade, idade e com quem moram."
            items={form.filhos}
            emptyItem={EMPTY_FILHO}
            disabled={disabled}
            onAdd={(empty) => addArrayItem("filhos", empty)}
            onRemove={(index) => removeArrayItem("filhos", index)}
          >
            {(_, index) => (
              <div className="ana-grid ana-grid--2">
                <Field label="Nome" value={form.filhos[index]?.nome} onChange={(v) => updateArrayItem("filhos", index, "nome", v)} disabled={disabled} />
                <Field label="Idade" value={form.filhos[index]?.idade} onChange={(v) => updateArrayItem("filhos", index, "idade", v)} disabled={disabled} />
                <BoolField label="Mora com o cliente?" value={form.filhos[index]?.moraComCliente} onChange={(v) => updateArrayItem("filhos", index, "moraComCliente", v)} disabled={disabled} />
                <Field label="Com quem mora?" value={form.filhos[index]?.comQuemMora} onChange={(v) => updateArrayItem("filhos", index, "comQuemMora", v)} disabled={disabled} />
                <TextArea label="Observações" value={form.filhos[index]?.observacoes} onChange={(v) => updateArrayItem("filhos", index, "observacoes", v)} disabled={disabled} rows={2} />
              </div>
            )}
          </Repeater>
        ) : null}
      </Section>

      <Section eyebrow="Histórico familiar" title="Relações e histórico familiar">
        <div className="ana-grid ana-grid--2">
          <TextArea label="Relação com pai" value={form.relacaoPai} onChange={(v) => update("relacaoPai", v)} disabled={disabled} rows={3} />
          <TextArea label="Relação com mãe" value={form.relacaoMae} onChange={(v) => update("relacaoMae", v)} disabled={disabled} rows={3} />
          <TextArea label="Relação com irmãos" value={form.relacaoIrmaos} onChange={(v) => update("relacaoIrmaos", v)} disabled={disabled} rows={3} />
          <TextArea label="Relação com filhos" value={form.relacaoFilhos} onChange={(v) => update("relacaoFilhos", v)} disabled={disabled} rows={3} />
          <TextArea label="Conflitos familiares relevantes" value={form.conflitosFamiliares} onChange={(v) => update("conflitosFamiliares", v)} disabled={disabled} rows={3} />
        </div>

        <BoolField label="Existe histórico de doenças na família?" value={form.possuiHistoricoDoencasFamilia} onChange={(v) => update("possuiHistoricoDoencasFamilia", v)} disabled={disabled} />

        <div className="ana-grid ana-grid--2">
          {form.possuiHistoricoDoencasFamilia === true ? (
            <TextArea label="Doenças na família" value={form.historicoDoencasFamilia} onChange={(v) => update("historicoDoencasFamilia", v)} disabled={disabled} rows={3} />
          ) : null}
          <TextArea label="Transtornos psicológicos/psiquiátricos na família" value={form.historicoTranstornosFamilia} onChange={(v) => update("historicoTranstornosFamilia", v)} disabled={disabled} rows={3} />
          <TextArea label="Dependência química na família" value={form.historicoDependenciaQuimicaFamilia} onChange={(v) => update("historicoDependenciaQuimicaFamilia", v)} disabled={disabled} rows={3} />
          <TextArea label="Histórico de suicídio na família" value={form.historicoSuicidioFamilia} onChange={(v) => update("historicoSuicidioFamilia", v)} disabled={disabled} rows={3} />
          <TextArea label="Violência familiar" value={form.historicoViolenciaFamilia} onChange={(v) => update("historicoViolenciaFamilia", v)} disabled={disabled} rows={3} />
        </div>
      </Section>

      <Section eyebrow="Rotina" title="Trabalho, estudo, sono, alimentação e hábitos">
        <div className="ana-grid ana-grid--2">
          <BoolField label="Trabalha atualmente?" value={form.trabalhaAtualmente} onChange={(v) => update("trabalhaAtualmente", v)} disabled={disabled} />
          <BoolField label="Estuda atualmente?" value={form.estudaAtualmente} onChange={(v) => update("estudaAtualmente", v)} disabled={disabled} />
        </div>

        {form.trabalhaAtualmente === true ? (
          <div className="ana-subpanel">
            <h3>Trabalho</h3>
            <div className="ana-grid ana-grid--2">
              <Field label="Profissão" value={form.trabalho.profissao} onChange={(v) => update("trabalho.profissao", v)} disabled={disabled} />
              <Field label="Onde trabalha?" value={form.trabalho.local} onChange={(v) => update("trabalho.local", v)} disabled={disabled} />
              <Field label="Função" value={form.trabalho.funcao} onChange={(v) => update("trabalho.funcao", v)} disabled={disabled} />
              <Field label="Carga horária" value={form.trabalho.cargaHoraria} onChange={(v) => update("trabalho.cargaHoraria", v)} disabled={disabled} />
              <Field label="Satisfação com o trabalho" value={form.trabalho.satisfacaoTrabalho} onChange={(v) => update("trabalho.satisfacaoTrabalho", v)} disabled={disabled} />
              <BoolField label="Está afastado pelo INSS?" value={form.trabalho.afastadoInss} onChange={(v) => update("trabalho.afastadoInss", v)} disabled={disabled} />
              <TextArea label="Conflitos no trabalho" value={form.trabalho.conflitosTrabalho} onChange={(v) => update("trabalho.conflitosTrabalho", v)} disabled={disabled} rows={3} />
              <TextArea label="Observações" value={form.trabalho.observacoes} onChange={(v) => update("trabalho.observacoes", v)} disabled={disabled} rows={3} />
            </div>
          </div>
        ) : null}

        {form.estudaAtualmente === true ? (
          <div className="ana-subpanel">
            <h3>Estudo</h3>
            <div className="ana-grid ana-grid--2">
              <Field label="Curso/série" value={form.estudo.cursoSerie} onChange={(v) => update("estudo.cursoSerie", v)} disabled={disabled} />
              <Field label="Instituição" value={form.estudo.instituicao} onChange={(v) => update("estudo.instituicao", v)} disabled={disabled} />
              <TextArea label="Dificuldades" value={form.estudo.dificuldades} onChange={(v) => update("estudo.dificuldades", v)} disabled={disabled} rows={3} />
              <TextArea label="Observações" value={form.estudo.observacoes} onChange={(v) => update("estudo.observacoes", v)} disabled={disabled} rows={3} />
            </div>
          </div>
        ) : null}

        <TextArea label="Rotina diária" value={form.rotina} onChange={(v) => update("rotina", v)} disabled={disabled} rows={4} />

        <div className="ana-grid ana-grid--2">
          <Field label="Qualidade do sono" value={form.sono.qualidade} onChange={(v) => update("sono.qualidade", v)} disabled={disabled} />
          <Field label="Horas por noite" value={form.sono.horasPorNoite} onChange={(v) => update("sono.horasPorNoite", v)} disabled={disabled} />
          <TextArea label="Dificuldades no sono" value={form.sono.dificuldades} onChange={(v) => update("sono.dificuldades", v)} disabled={disabled} rows={3} />
          <TextArea label="Observações do sono" value={form.sono.observacoes} onChange={(v) => update("sono.observacoes", v)} disabled={disabled} rows={3} />
          <Field label="Qualidade da alimentação" value={form.alimentacao.qualidade} onChange={(v) => update("alimentacao.qualidade", v)} disabled={disabled} />
          <Field label="Apetite" value={form.alimentacao.apetite} onChange={(v) => update("alimentacao.apetite", v)} disabled={disabled} />
          <TextArea label="Observações da alimentação" value={form.alimentacao.observacoes} onChange={(v) => update("alimentacao.observacoes", v)} disabled={disabled} rows={3} />
          <BoolField label="Pratica atividade física?" value={form.atividadeFisica.pratica} onChange={(v) => update("atividadeFisica.pratica", v)} disabled={disabled} />
          {form.atividadeFisica.pratica === true ? (
            <Field label="Frequência" value={form.atividadeFisica.frequencia} onChange={(v) => update("atividadeFisica.frequencia", v)} disabled={disabled} />
          ) : null}
          <TextArea label="Observações sobre atividade física" value={form.atividadeFisica.observacoes} onChange={(v) => update("atividadeFisica.observacoes", v)} disabled={disabled} rows={3} />
        </div>

        <div className="ana-subpanel ana-subpanel--soft">
          <h3>Uso de substâncias</h3>
          <div className="ana-grid ana-grid--3">
            <BoolField label="Álcool" value={form.usoSubstancias.alcool} onChange={(v) => update("usoSubstancias.alcool", v)} disabled={disabled} />
            <BoolField label="Tabaco" value={form.usoSubstancias.tabaco} onChange={(v) => update("usoSubstancias.tabaco", v)} disabled={disabled} />
            <BoolField label="Outras drogas" value={form.usoSubstancias.outrasDrogas} onChange={(v) => update("usoSubstancias.outrasDrogas", v)} disabled={disabled} />
          </div>
          <div className="ana-grid ana-grid--2">
            {form.usoSubstancias.alcool === true ? <TextArea label="Detalhes sobre álcool" value={form.usoSubstancias.alcoolDetalhes} onChange={(v) => update("usoSubstancias.alcoolDetalhes", v)} disabled={disabled} rows={3} /> : null}
            {form.usoSubstancias.tabaco === true ? <TextArea label="Detalhes sobre tabaco" value={form.usoSubstancias.tabacoDetalhes} onChange={(v) => update("usoSubstancias.tabacoDetalhes", v)} disabled={disabled} rows={3} /> : null}
            {form.usoSubstancias.outrasDrogas === true ? <TextArea label="Detalhes sobre outras drogas" value={form.usoSubstancias.outrasDrogasDetalhes} onChange={(v) => update("usoSubstancias.outrasDrogasDetalhes", v)} disabled={disabled} rows={3} /> : null}
            <TextArea label="Observações" value={form.usoSubstancias.observacoes} onChange={(v) => update("usoSubstancias.observacoes", v)} disabled={disabled} rows={3} />
          </div>
        </div>
      </Section>
    </div>
  );
}

function PhaseEventos({ form, update, updateArrayItem, addArrayItem, removeArrayItem, disabled }) {
  return (
    <div className="ana-stack">
      <Section eyebrow="Eventos" title="Eventos marcantes da história de vida">
        <div className="ana-grid ana-grid--2">
          <BoolField label="Teve morte de ente querido?" value={form.teveMorteEnteQuerido} onChange={(v) => update("teveMorteEnteQuerido", v)} disabled={disabled} />
          <BoolField label="Teve desemprego na família?" value={form.teveDesempregoFamilia} onChange={(v) => update("teveDesempregoFamilia", v)} disabled={disabled} />
        </div>

        {form.teveMorteEnteQuerido === true ? (
          <Repeater
            title="Morte de ente querido"
            desc="Registre quando ocorreu, motivo e impacto emocional."
            items={form.mortesEntesQueridos}
            emptyItem={{ ...EMPTY_EVENTO, tipo: "morte_ente_querido" }}
            disabled={disabled}
            onAdd={(empty) => addArrayItem("mortesEntesQueridos", empty)}
            onRemove={(index) => removeArrayItem("mortesEntesQueridos", index)}
          >
            {(_, index) => <EventoFields path="mortesEntesQueridos" index={index} form={form} updateArrayItem={updateArrayItem} disabled={disabled} />}
          </Repeater>
        ) : null}

        {form.teveDesempregoFamilia === true ? (
          <Repeater
            title="Desemprego familiar"
            desc="Registre quem, quando e impacto na família."
            items={form.desempregosFamilia}
            emptyItem={{ ...EMPTY_EVENTO, tipo: "desemprego" }}
            disabled={disabled}
            onAdd={(empty) => addArrayItem("desempregosFamilia", empty)}
            onRemove={(index) => removeArrayItem("desempregosFamilia", index)}
          >
            {(_, index) => <EventoFields path="desempregosFamilia" index={index} form={form} updateArrayItem={updateArrayItem} disabled={disabled} />}
          </Repeater>
        ) : null}

        <Repeater
          title="Evento marcante"
          desc="Separações, acidentes, mudanças, adoecimento grave, perdas ou outros eventos relevantes."
          items={form.eventosMarcantes}
          emptyItem={EMPTY_EVENTO}
          disabled={disabled}
          onAdd={(empty) => addArrayItem("eventosMarcantes", empty)}
          onRemove={(index) => removeArrayItem("eventosMarcantes", index)}
        >
          {(_, index) => <EventoFields path="eventosMarcantes" index={index} form={form} updateArrayItem={updateArrayItem} disabled={disabled} />}
        </Repeater>
      </Section>

      <Section
        eyebrow="Risco"
        title="Risco e segurança"
        desc="Bloco sensível. Use para registro clínico e definição de plano de segurança quando necessário."
        tone="risk"
      >
        <div className="ana-risk-alert">
          <strong>Atenção clínica</strong>
          <span>
            Este bloco não substitui avaliação profissional. Registre sinais de alerta, plano de segurança e encaminhamentos quando aplicável.
          </span>
        </div>

        <div className="ana-grid ana-grid--3">
          <BoolField label="Já teve pensamento suicida?" value={form.tevePensamentoSuicida} onChange={(v) => update("tevePensamentoSuicida", v)} disabled={disabled} />
          <BoolField label="Pensamento suicida atual?" value={form.pensamentoSuicidaAtual} onChange={(v) => update("pensamentoSuicidaAtual", v)} disabled={disabled} />
          <BoolField label="Já tentou suicídio?" value={form.jaTentouSuicidio} onChange={(v) => update("jaTentouSuicidio", v)} disabled={disabled} />
          <BoolField label="Existe plano?" value={form.existePlanoSuicida} onChange={(v) => update("existePlanoSuicida", v)} disabled={disabled} />
          <BoolField label="Acesso a meios?" value={form.acessoAMeios} onChange={(v) => update("acessoAMeios", v)} disabled={disabled} />
          <BoolField label="Automutilação?" value={form.automutilacao} onChange={(v) => update("automutilacao", v)} disabled={disabled} />
        </div>

        <div className="ana-grid ana-grid--2">
          {form.jaTentouSuicidio === true ? (
            <TextArea label="Detalhes da tentativa" value={form.tentativaSuicidioDetalhes} onChange={(v) => update("tentativaSuicidioDetalhes", v)} disabled={disabled} rows={3} />
          ) : null}
          {form.automutilacao === true ? (
            <TextArea label="Detalhes sobre automutilação" value={form.automutilacaoDetalhes} onChange={(v) => update("automutilacaoDetalhes", v)} disabled={disabled} rows={3} />
          ) : null}
          <TextArea label="Histórico de violência" value={form.historicoViolencia} onChange={(v) => update("historicoViolencia", v)} disabled={disabled} rows={3} />
          <SelectField
            label="Nível de risco"
            value={form.nivelRisco}
            onChange={(v) => update("nivelRisco", v)}
            disabled={disabled}
            options={[
              { value: "baixo", label: "Baixo" },
              { value: "moderado", label: "Moderado" },
              { value: "alto", label: "Alto" },
              { value: "critico", label: "Crítico" },
            ]}
          />
          <TextArea label="Plano de segurança" value={form.planoSeguranca} onChange={(v) => update("planoSeguranca", v)} disabled={disabled} rows={4} />
          <TextArea label="Observações de risco" value={form.observacoesRisco} onChange={(v) => update("observacoesRisco", v)} disabled={disabled} rows={4} />
        </div>
      </Section>
    </div>
  );
}

function EventoFields({ path, index, form, updateArrayItem, disabled }) {
  const item = getByPath(form, `${path}.${index}`) || {};

  return (
    <div className="ana-grid ana-grid--2">
      <Field label="Tipo" value={item.tipo} onChange={(v) => updateArrayItem(path, index, "tipo", v)} disabled={disabled} placeholder="Ex.: morte, desemprego, separação..." />
      <Field label="Quando" value={item.quando} onChange={(v) => updateArrayItem(path, index, "quando", v)} disabled={disabled} />
      <TextArea label="Descrição" value={item.descricao} onChange={(v) => updateArrayItem(path, index, "descricao", v)} disabled={disabled} rows={3} />
      <TextArea label="Motivo" value={item.motivo} onChange={(v) => updateArrayItem(path, index, "motivo", v)} disabled={disabled} rows={3} />
      <TextArea label="Impacto" value={item.impacto} onChange={(v) => updateArrayItem(path, index, "impacto", v)} disabled={disabled} rows={3} />
      <TextArea label="Observações" value={item.observacoes} onChange={(v) => updateArrayItem(path, index, "observacoes", v)} disabled={disabled} rows={3} />
    </div>
  );
}

function PhaseFinalizacao({ form, update, progress, disabled }) {
  return (
    <div className="ana-stack">
      <Section eyebrow="Conclusão" title="Objetivos terapêuticos e observações clínicas">
        <TextArea
          label="Objetivos terapêuticos"
          value={form.objetivosTerapeuticos}
          onChange={(v) => update("objetivosTerapeuticos", v)}
          disabled={disabled}
          rows={4}
          placeholder="Ex.: manejo da ansiedade, regulação emocional, fortalecimento de estratégias de enfrentamento."
        />

        <div className="ana-grid ana-grid--2">
          <TextArea label="Impressões iniciais" value={form.impressoesIniciais} onChange={(v) => update("impressoesIniciais", v)} disabled={disabled} rows={4} />
          <TextArea label="Hipóteses iniciais" value={form.hipotesesIniciais} onChange={(v) => update("hipotesesIniciais", v)} disabled={disabled} rows={4} />
          <TextArea label="Pontos de atenção" value={form.pontosAtencao} onChange={(v) => update("pontosAtencao", v)} disabled={disabled} rows={4} />
          <TextArea label="Encaminhamentos" value={form.encaminhamentos} onChange={(v) => update("encaminhamentos", v)} disabled={disabled} rows={4} />
          <TextArea label="Combinados iniciais" value={form.combinadosIniciais} onChange={(v) => update("combinadosIniciais", v)} disabled={disabled} rows={4} />
          <TextArea label="Observações finais" value={form.observacoesFinais} onChange={(v) => update("observacoesFinais", v)} disabled={disabled} rows={4} />
        </div>
      </Section>

      <Section eyebrow="Revisão" title="Resumo antes de finalizar">
        <div className="ana-review">
          <div className="ana-review__score">
            <span>Preenchimento</span>
            <strong>{progress.percent}%</strong>
            <small>
              {progress.done} de {progress.total} blocos com alguma informação.
            </small>
          </div>

          <div className="ana-review__list">
            {progress.sections.map((section) => (
              <div key={section.key} className={section.done ? "done" : "pending"}>
                <b>{section.done ? "✓" : "•"}</b>
                <span>{section.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ana-final-note">
          <strong>Como funciona a finalização?</strong>
          <span>
            A anamnese finalizada fica bloqueada para edição direta. Se a profissional precisar complementar depois, basta reabrir para edição, atualizar e finalizar novamente.
          </span>
        </div>
      </Section>
    </div>
  );
}
